<template>
    <tailwind-layout title="Command Center">

        <div class="-mt-4 sm:-mt-6">

        <!-- ── Announcement banner ── -->
        <div v-if="showRiskGuardBanner" class="mb-6 flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                <font-awesome-icon :icon="faStars" class="shrink-0 text-blue-500" />
                <span class="text-sm text-gray-700">
                    Lease agreements, E-sign and legal consulting access are <span class="font-semibold">now live!</span> Add a property, Select the 'Lease Agreements' tab and click 'Create New Lease' to get started 🎉
                </span>
            </div>
            <button @click="dismissRiskGuardBanner" class="ml-4 shrink-0 text-gray-400 hover:text-gray-600">
                <font-awesome-icon :icon="faXmark" />
            </button>
        </div>

        <!-- ── Header card ── -->
        <div class="mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div class="flex items-start justify-between p-6">
                <!-- Left: greeting + stats -->
                <div class="min-w-0 flex-1">
                    <h1 class="text-2xl font-bold text-gray-900">{{ greeting }}, {{ firstName }}! 👋</h1>
                    <p class="mt-1 text-sm text-gray-500">Here's what's happening with your properties.</p>

                    <!-- Quick-action blocks -->
                    <div class="mt-6 flex flex-col items-start gap-5 lg:flex-row lg:gap-0 lg:divide-x lg:divide-gray-100">

                        <!-- Add a Property -->
                        <div class="flex shrink-0 items-start gap-3 lg:pr-6">
                            <div class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100">
                                <font-awesome-icon :icon="faPlus" class="text-blue-600" />
                            </div>
                            <div class="min-w-0">
                                <div class="whitespace-nowrap text-sm font-bold text-gray-900">Add a Property</div>
                                <div class="whitespace-nowrap text-sm text-gray-500">to start using features</div>
                                <inertia-link :href="route('properties.index')" class="mt-1 block whitespace-nowrap text-sm font-semibold text-blue-600 hover:underline">
                                    Add Property →
                                </inertia-link>
                            </div>
                        </div>

                        <!-- Create a Rule -->
                        <div class="flex shrink-0 items-start gap-3 lg:px-6">
                            <div class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100">
                                <font-awesome-icon :icon="faFileLines" class="text-blue-600" />
                            </div>
                            <div class="min-w-0">
                                <div class="whitespace-nowrap text-sm font-bold text-gray-900">Create a Rule</div>
                                <div class="whitespace-nowrap text-sm text-gray-500">for incoming applicants</div>
                                <inertia-link :href="route('screening-rules.edit')" class="mt-1 block whitespace-nowrap text-sm font-semibold text-blue-600 hover:underline">
                                    Create Rule →
                                </inertia-link>
                            </div>
                        </div>

                        <!-- Set Max Risk Reduction -->
                        <div class="flex shrink-0 items-start gap-3 lg:pl-6">
                            <div class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100">
                                <font-awesome-icon :icon="faUmbrella" class="text-blue-600" />
                            </div>
                            <div class="min-w-0">
                                <div class="whitespace-nowrap text-sm font-bold text-gray-900">{{ riskReductionTitle }}</div>
                                <div
                                    v-if="riskReductionEnabled"
                                    class="mt-0.5 flex items-center gap-1 whitespace-nowrap text-sm"
                                    :class="riskReductionStatusClass"
                                >
                                    <font-awesome-icon :icon="riskReductionStatusIcon" class="shrink-0" />
                                    <span>{{ riskReductionEnabledLabel }}</span>
                                </div>
                                <div v-else class="whitespace-nowrap text-sm text-gray-500">{{ riskReductionSubtitle }}</div>
                                <button
                                    type="button"
                                    @click="openRiskUmbrella"
                                    class="mt-1 block whitespace-nowrap text-left text-sm font-semibold hover:underline"
                                    :class="riskReductionEnabled ? 'text-gray-600' : 'text-blue-600'"
                                >
                                    {{ riskReductionCta }}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- Right: illustration -->
                <div class="ml-6 hidden h-44 w-64 shrink-0 overflow-hidden rounded-2xl lg:flex items-center justify-center">
                    <img :src="dashboardIllustration" alt="Properties illustration" class="h-full w-full object-contain" />
                </div>
            </div>
        </div>

        <!-- ── Main 3-column tiles ── -->
        <div class="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">

            <!-- Tenant Screening -->
            <div class="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div class="flex items-start gap-3">
                    <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-100">
                        <font-awesome-icon :icon="faHouseUser" class="text-2xl text-orange-500" />
                    </div>
                    <div>
                        <h2 class="text-sm font-bold uppercase tracking-wide text-gray-900">Tenant Screening</h2>
                        <p class="mt-0.5 text-xs text-gray-500">Stay on top of your applications</p>
                    </div>
                </div>

                <div class="mt-6 flex-1">
                    <div class="text-6xl font-bold leading-none" :class="active_applications > 0 ? 'text-orange-500' : 'text-gray-300'">
                        {{ active_applications }}
                    </div>
                    <div class="mt-2 text-sm font-medium text-gray-700">Active Applications</div>
                    <div class="mt-2 text-sm" :class="applications_new_this_week > 0 ? 'text-gray-500' : 'text-gray-400'">
                        {{ applications_new_this_week > 0 ? `🔥 ${applications_new_this_week} new this week` : 'No new applications this week' }}
                    </div>
                </div>

                <inertia-link
                    :href="route('properties.index')"
                    class="mt-6 flex items-center justify-between rounded-xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-700"
                >
                    Review Applications
                    <font-awesome-icon :icon="faChevronRight" />
                </inertia-link>
            </div>

            <!-- Rent Payments -->
            <div class="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div class="flex items-start gap-3">
                    <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-100">
                        <font-awesome-icon :icon="faSquareDollar" class="text-2xl text-green-600" />
                    </div>
                    <div>
                        <h2 class="text-sm font-bold uppercase tracking-wide text-gray-900">Rent Payments</h2>
                        <p class="mt-0.5 text-xs text-gray-500">Track rent collections and history</p>
                    </div>
                </div>

                <div class="mt-6 flex-1">
                    <div class="text-6xl font-bold leading-none" :class="rental_payment_properties_total > 0 ? 'text-green-600' : 'text-gray-300'">
                        ${{ rent_amount }}
                    </div>
                    <div class="mt-2 text-sm font-medium text-gray-700">
                        <span v-if="next_rent_due_date">Next rent due on {{ next_rent_due_date }}</span>
                        <span v-else>No upcoming rent due</span>
                    </div>
                    <div class="mt-2 flex items-center gap-1.5 text-sm" :class="past_due_properties_count > 0 ? 'text-red-500' : 'text-green-600'">
                        <font-awesome-icon :icon="past_due_properties_count > 0 ? faTriangleExclamation : faCircleCheck" />
                        {{ past_due_properties_count }} overdue payment{{ past_due_properties_count !== 1 ? 's' : '' }}
                    </div>
                </div>

                <inertia-link
                    :href="route('properties.index')"
                    class="mt-6 flex items-center justify-between rounded-xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-700"
                >
                    View Payments
                    <font-awesome-icon :icon="faChevronRight" />
                </inertia-link>
            </div>

            <!-- Maintenance -->
            <div class="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div class="flex items-start gap-3">
                    <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-100">
                        <font-awesome-icon :icon="faScrewdriverWrench" class="text-2xl text-red-500" />
                    </div>
                    <div>
                        <h2 class="text-sm font-bold uppercase tracking-wide text-gray-900">Maintenance</h2>
                        <p class="mt-0.5 text-xs text-gray-500">Resolve issues and keep tenants happy</p>
                    </div>
                </div>

                <div class="mt-6 flex-1">
                    <div class="text-6xl font-bold leading-none" :class="active_maintenance_requests > 0 ? 'text-red-500' : 'text-gray-300'">
                        {{ active_maintenance_requests }}
                    </div>
                    <div class="mt-2 text-sm font-medium text-gray-700">Open Requests</div>
                    <div class="mt-2 flex items-center gap-1.5 text-sm" :class="new_maintenance_requests > 0 ? 'text-orange-500' : 'text-green-600'">
                        <font-awesome-icon :icon="new_maintenance_requests > 0 ? faTriangleExclamation : faCircleCheck" />
                        {{ new_maintenance_requests > 0 ? `${new_maintenance_requests} needs attention` : 'All requests handled' }}
                    </div>
                </div>

                <inertia-link
                    :href="route('properties.index')"
                    class="mt-6 flex items-center justify-between rounded-xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-700"
                >
                    Resolve Requests
                    <font-awesome-icon :icon="faChevronRight" />
                </inertia-link>
            </div>
        </div>

        <!-- ── Secondary 4-column cards ── -->
        <div class="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">

            <!-- Insurance -->
            <div class="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div class="mb-4 flex items-center gap-3">
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <font-awesome-icon :icon="faShieldCheck" class="text-blue-600" />
                    </div>
                    <h3 class="text-xs font-bold uppercase tracking-wide text-gray-800">Insurance</h3>
                </div>
                <p class="flex-1 text-xs text-gray-500">Protect your property and yourself with landlord insurance.</p>
                <inertia-link
                    :href="route('properties.index')"
                    class="mt-4 flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    View Insurance <font-awesome-icon :icon="faChevronRight" class="text-xs" />
                </inertia-link>
            </div>

            <!-- Earn Rewards -->
            <div class="relative flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <span
                    v-if="copiedAffiliateLink"
                    class="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/95 text-sm font-semibold text-green-700"
                >Copied!</span>
                <div class="mb-4 flex items-center gap-3">
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-100">
                        <font-awesome-icon :icon="faUserGroup" class="text-purple-600" />
                    </div>
                    <h3 class="text-xs font-bold uppercase tracking-wide text-gray-800">Earn Rewards</h3>
                </div>
                <p class="flex-1 text-xs text-gray-500">Share RentRisk and earn rewards with our affiliate program.</p>
                <button
                    @click="copyAffiliateLinkToClipboard"
                    class="mt-4 flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    Copy Link <font-awesome-icon :icon="faChevronRight" class="text-xs" />
                </button>
            </div>

            <!-- Articles -->
            <div class="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div class="mb-4 flex items-center gap-3">
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100">
                        <font-awesome-icon :icon="faPencil" class="text-teal-600" />
                    </div>
                    <h3 class="text-xs font-bold uppercase tracking-wide text-gray-800">Articles</h3>
                </div>
                <p class="flex-1 text-xs text-gray-500">Share your industry knowledge and experience by contributing to RentRisk's blog.</p>
                <inertia-link
                    :href="route('blogs.index')"
                    class="mt-4 flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    Write an Article <font-awesome-icon :icon="faChevronRight" class="text-xs" />
                </inertia-link>
            </div>

            <!-- Need Help? -->
            <div class="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div class="mb-4 flex items-center gap-3">
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100">
                        <font-awesome-icon :icon="faHeadset" class="text-orange-500" />
                    </div>
                    <h3 class="text-xs font-bold uppercase tracking-wide text-gray-800">Need Help?</h3>
                </div>
                <p class="flex-1 text-xs text-gray-500">Check our knowledge base or get in touch with our support team.</p>
                <inertia-link
                    :href="route('resources.index')"
                    class="mt-4 flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    Get Support <font-awesome-icon :icon="faChevronRight" class="text-xs" />
                </inertia-link>
            </div>
        </div>

        <!-- ── Pro Tip ── -->
        <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
            <div class="flex items-center gap-3">
                <font-awesome-icon :icon="faLightbulb" class="shrink-0 text-yellow-400" />
                <span class="text-sm text-gray-700">
                    <span class="font-semibold">Pro Tip:</span> Join the 'Military Rentals' Facebook group for management tips, active rental listings and real time Q&amp;A
                </span>
            </div>
            <a
                href="https://www.facebook.com/groups/militaryrentals"
                target="_blank"
                rel="noopener noreferrer"
                class="ml-4 shrink-0 text-sm font-semibold text-gray-900 hover:underline"
            >
                Request Access →
            </a>
        </div>

        <!-- Share Property Modal -->
        <share-modal
            :show="showPropertyShareModal"
            :share-url="selectedProperty ? selectedProperty.application_link : ''"
            :share-text="selectedProperty ? `${selectedProperty.address_1} is available for rent in ${selectedProperty.city}, ${selectedProperty.state}. Apply here today!` : ''"
            title="Share Property Application Link"
            @close="closeShareModal"
        />

        <UserLevelAutoEsoFlow
            ref="userAutoEsoFlow"
            :user-global-auto-eso="user_global_auto_eso"
            :is_agent_payment_method_added="is_agent_payment_method_added"
            :user_payment_method="user_payment_method"
            :stripe_data="stripe_data"
        />

        </div>

    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ShareModal from './Properties/ShareModal.vue'
import UserLevelAutoEsoFlow from '@/Components/UserLevelAutoEsoFlow.vue'
import { router } from '@inertiajs/vue3'
import dashboardIllustration from '../../images/dashboard-illustration.png'

import {
    faHouseUser,
    faShieldCheck,
    faSquareDollar,
    faScrewdriverWrench,
    faChevronRight,
} from '@fortawesome/pro-light-svg-icons'

import {
    faStars,
    faXmark,
    faCircleCheck,
    faTriangleExclamation,
    faFileLines,
    faPlus,
    faUmbrella,
    faUserGroup,
    faPencil,
    faHeadset,
    faLightbulb,
} from '@fortawesome/pro-solid-svg-icons'

export default {
    components: {
        TailwindLayout,
        FontAwesomeIcon,
        ShareModal,
        UserLevelAutoEsoFlow,
    },

    props: {
        properties: Object,
        cta_property_id: [Number, String, null],
        active_properties: Number,
        approved_applicants: Number,
        active_applications: Number,
        applications_new_this_week: Number,
        rental_payment_properties_total: Number,
        rental_payment_properties: Object,
        rent_payment_properties: Object,
        rent_amount: [Number, String],
        next_rent_due_date: String,
        past_due_rent_amount: Number,
        past_due_properties_count: Number,
        new_maintenance_requests: Number,
        active_maintenance_requests: Number,
        resolved_maintenance_requests: Number,
        sure_insurance_url: String,
        partner_affiliate_link: String,
        is_new_user: Boolean,
        vacancies: Number,
        collection_rate: Number,
        user_global_auto_eso: {
            type: Object,
            default: () => ({
                is_active: false,
                payer: null,
                effective_billing: false,
                payment_method_required_but_missing: false,
            }),
        },
        is_agent_payment_method_added: { type: Boolean, default: false },
        user_payment_method: { type: Object, default: null },
        stripe_data: { type: [Object, Array], default: () => [] },
    },

    data() {
        return {
            faHouseUser,
            faShieldCheck,
            faSquareDollar,
            faScrewdriverWrench,
            faChevronRight,
            faStars,
            faXmark,
            faCircleCheck,
            faTriangleExclamation,
            faFileLines,
            faPlus,
            faUmbrella,
            faUserGroup,
            faPencil,
            faHeadset,
            faLightbulb,
            dashboardIllustration,
            showPropertyShareModal: false,
            selectedProperty: null,
            copiedAffiliateLink: false,
            showRiskGuardBanner: !localStorage.getItem('leaseLiveBannerDismissed'),
        }
    },

    computed: {
        greeting() {
            const hour = new Date().getHours()
            if (hour < 12) { return 'Good morning' }
            if (hour < 17) { return 'Good afternoon' }
            return 'Good evening'
        },

        firstName() {
            const name = this.$page.props.auth?.user?.name ?? ''
            return name.split(' ')[0]
        },

        setupPaymentsHref() {
            if (this.cta_property_id) {
                return route('properties.show', { property: this.cta_property_id, tab: 'rent-payments' })
            }
            return route('properties.index')
        },

        riskReductionEnabled() {
            const eso = this.user_global_auto_eso
            return Boolean(eso?.is_active) || eso?.payer != null
        },

        riskReductionTitle() {
            return this.riskReductionEnabled
                ? 'Max Risk Reduction'
                : 'Set Max Risk Reduction'
        },

        riskReductionSubtitle() {
            return 'for all properties'
        },

        riskReductionEnabledLabel() {
            if (this.user_global_auto_eso?.payment_method_required_but_missing) {
                return 'Enabled · inactive'
            }
            return 'Enabled for all properties'
        },

        riskReductionStatusClass() {
            if (this.user_global_auto_eso?.payment_method_required_but_missing) {
                return 'text-amber-600'
            }
            return 'text-green-600'
        },

        riskReductionStatusIcon() {
            if (this.user_global_auto_eso?.payment_method_required_but_missing) {
                return this.faTriangleExclamation
            }
            return this.faCircleCheck
        },

        riskReductionCta() {
            if (this.riskReductionEnabled) {
                return 'Turn off →'
            }
            return 'Enable for All Properties →'
        },
    },

    methods: {
        dismissRiskGuardBanner() {
            this.showRiskGuardBanner = false
            localStorage.setItem('leaseLiveBannerDismissed', '1')
        },

        openRiskUmbrella() {
            const flow = this.$refs.userAutoEsoFlow
            if (!flow) {
                return
            }
            if (this.riskReductionEnabled) {
                flow.openDisable()
                return
            }
            flow.openEnable()
        },

        openShareModal(property) {
            this.selectedProperty = property
            this.showPropertyShareModal = true
        },

        closeShareModal() {
            this.showPropertyShareModal = false
            this.selectedProperty = null
        },

        async copyAffiliateLinkToClipboard() {
            if (!this.partner_affiliate_link) {
                router.visit(route('partners.index'))
                return
            }
            try {
                await navigator.clipboard.writeText(this.partner_affiliate_link)
                this.copiedAffiliateLink = true
                setTimeout(() => { this.copiedAffiliateLink = false }, 2000)
            } catch {
                alert('Cannot copy')
            }
        },
    },
}
</script>
